import { useEffect, useRef, useState } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import { CinematicHero, ReadingProgress, Reveal, SentinelManifesto, useLandingScroll } from './SentinelMotion';
import {
  ArrowUpRight, Check, Command, Crown, Globe2, Layers3, Loader2,
  LockKeyhole, MessageSquare, Mic, Plus, ScrollText, ShieldCheck, Sparkles, UserPlus, Users, X, Zap,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useI18n } from '../contexts/I18nContext';
import { buildDashboardPath, getPreferredLang, normalizeLang } from '../lib/routes';
import './LoginPage.css';

const ASSETS = (process.env.PUBLIC_URL || '') + '/branding';

const SYSTEMS = [
  {
    id: 'levels', icon: Zap, label: ['المستويات', 'Leveling'],
    title: ['كل مشاركة تستحق أن تُحسب.', 'Make every contribution count.'],
    description: ['حوّل نشاط مجتمعك إلى تقدّم يستحقه أعضاؤه. نظام خبرة مرن يجمع المحادثات والنشاط الصوتي والتفاعلات، ويكافئ المشاركة برتب ومزايا تختارها أنت.', 'Turn community activity into meaningful progress. Bring messages, voice activity, and reactions into one flexible XP system, with the role rewards you choose.'],
    points: [
      ['مصادر خبرة وحدود تهدئة قابلة للتخصيص', 'Custom XP sources and cooldowns'],
      ['مكافآت رتب وبونص وفعاليات خبرة', 'Role rewards, bonuses, and XP events'],
      ['لوحة متصدرين وإدارة تقدّم الأعضاء', 'Leaderboards and member progress management'],
    ],
  },
  {
    id: 'moderation', icon: ShieldCheck, label: ['الحماية', 'Moderation'],
    title: ['مجتمعك. وقواعدك.', 'Your community. Your rules.'],
    description: ['امنح فريقك أدوات واضحة للتعامل مع المخالفات. اضبط قواعد الإشراف الآلي والاستثناءات ونقاط المخالفات بما يناسب طبيعة سيرفرك.', 'Give your team a clear framework for moderation. Configure automatic rules, exemptions, and violation points around the needs of your server.'],
    points: [
      ['قواعد إشراف آلي قابلة للتخصيص', 'Configurable automatic moderation rules'],
      ['نظام نقاط لتتبّع المخالفات', 'A points system for tracking violations'],
      ['استثناءات للقنوات والرتب', 'Channel and role exemptions'],
    ],
  },
  {
    id: 'welcome', icon: UserPlus, label: ['الترحيب', 'Welcome'],
    title: ['بداية تليق بمجتمعك.', 'A proper first impression.'],
    description: ['استقبل الأعضاء الجدد بطابع سيرفرك. خصّص رسائل الترحيب وصورها وإشعارات المغادرة، واجعل أول لحظة جزءًا من هوية مجتمعك.', 'Welcome new members in your own style. Customize welcome messages, images, and departure notices to make the first moment feel like part of your community.'],
    points: [
      ['رسائل مخصصة لانضمام الأعضاء', 'Personalized member welcome messages'],
      ['صور ترحيب قابلة للتخصيص', 'Customizable welcome images'],
      ['إعدادات مستقلة لرسائل المغادرة', 'Separate departure message settings'],
    ],
  },
  {
    id: 'logs', icon: ScrollText, label: ['السجلات', 'Audit logs'],
    title: ['الصورة الكاملة بين يديك.', 'Keep the whole picture in view.'],
    description: ['تابع أحداث سيرفرك من مكان واحد. حدّد ما تريد تسجيله والقنوات المخصصة له، وامنح فريق الإدارة مرجعًا واضحًا لما يحدث.', 'Keep track of server events in one place. Choose what to log and where it belongs, giving your moderation team a clear reference for what happened.'],
    points: [
      ['إعدادات مخصصة لفئات السجلات', 'Configurable log categories'],
      ['توجيه السجلات إلى القنوات المناسبة', 'Route logs to the right channels'],
      ['مراجعة الأحداث من لوحة التحكم', 'Review events from the dashboard'],
    ],
  },
];

const Brand = () => (
  <span className="sentinel-brand">
    <img src={ASSETS + '/tns-sentinel-logo.png'} alt="" width="45" height="45" />
    <span>TNS <span>Sentinel</span></span>
  </span>
);

const SystemPreview = ({ id, t }) => (
  <div className="sentinel-demo">
    <div className="sentinel-demo-header">
      <span><Layers3 size={14} /> TNS / {t('لوحة التحكم', 'Control panel')}</span>
      <small>{t('معاينة توضيحية', 'Illustrative preview')}</small>
    </div>
    <div className="sentinel-demo-body" key={id}>
      {id === 'levels' && <>
        <div className="sentinel-demo-profile">
          <span className="sentinel-demo-avatar">S</span>
          <div><strong>Sentinel Member</strong><small>{t('رحلة التقدّم', 'A journey of progress')}</small></div>
          <div className="sentinel-level-number">12<small>{t('المستوى', 'LEVEL')}</small></div>
        </div>
        <div className="sentinel-xp-labels"><span>{t('المستوى التالي', 'Next level')}</span><span dir="ltr">3,600 / 5,000 XP</span></div>
        <div className="sentinel-meter" role="meter" aria-label={t('تقدّم توضيحي للمستوى التالي', 'Example progress to next level')} aria-valuemin={0} aria-valuemax={5000} aria-valuenow={3600}><span /></div>
        <div className="sentinel-demo-row"><span><MessageSquare size={15} />{t('المشاركة في المحادثات', 'Message activity')}</span><strong>{t('خبرة الرسائل', 'Message XP')}</strong></div>
        <div className="sentinel-demo-row"><span><Mic size={15} />{t('النشاط الصوتي', 'Time in voice')}</span><strong>{t('خبرة الصوت', 'Voice XP')}</strong></div>
        <div className="sentinel-demo-row"><span><Crown size={15} />{t('مكافأة المستوى', 'Level reward')}</span><strong>Vanguard</strong></div>
      </>}
      {id === 'moderation' && <>
        <p className="sentinel-demo-note"><ShieldCheck size={20} />{t('قواعد مجتمعك', 'Community rules')}</p>
        <div className="sentinel-demo-row"><span>{t('فلترة الكلمات', 'Word filter')}</span><strong>{t('قاعدة مفعّلة', 'Rule enabled')}</strong></div>
        <div className="sentinel-demo-row"><span>{t('مراقبة الروابط', 'Link moderation')}</span><strong>{t('قاعدة مفعّلة', 'Rule enabled')}</strong></div>
        <div className="sentinel-demo-row"><span>{t('نقاط المخالفات', 'Violation points')}</span><strong className="sentinel-warning">3 / 10</strong></div>
        <div className="sentinel-demo-row"><span>{t('استثناءات الرتب', 'Role exemptions')}</span><strong>Moderators</strong></div>
        <div className="sentinel-demo-row"><span><ScrollText size={15} />{t('قناة السجل', 'Log channel')}</span><strong dir="ltr"># moderation-log</strong></div>
      </>}
      {id === 'welcome' && <>
        <p className="sentinel-demo-note"><MessageSquare size={18} /><span dir="ltr"># welcome</span></p>
        <div className="sentinel-welcome-banner"><h4>{t('أهلًا بك في مجتمعنا.', 'You belong here.')}</h4><p>{t('بداية جديدة، وأشخاص بانتظارك.', 'A fresh start. Your people are waiting.')}</p></div>
        <div className="sentinel-demo-profile"><span className="sentinel-demo-avatar">S</span><div><strong>{t('أهلًا، Sentinel Member', 'Welcome, Sentinel Member')}</strong><small>{t('سعيدون بانضمامك إلينا.', 'Glad to have you with us.')}</small></div></div>
        <div className="sentinel-demo-row"><span><UserPlus size={15} />{t('رسالة الانضمام', 'Join message')}</span><strong>{t('مخصصة لسيرفرك', 'Made for your server')}</strong></div>
      </>}
      {id === 'logs' && <>
        <p className="sentinel-demo-note"><ScrollText size={18} />{t('سجل أحداث السيرفر', 'Server event log')}</p>
        {[
          [UserPlus, t('انضمام عضو', 'Member joined'), '14:32'],
          [Crown, t('تحديث رتبة', 'Role updated'), '14:28'],
          [MessageSquare, t('تعديل رسالة', 'Message edited'), '14:24'],
          [ShieldCheck, t('إجراء إشراف', 'Moderation action'), '14:20'],
        ].map(([Icon, label, time]) => <div className="sentinel-demo-row" key={time}><span><span className="sentinel-log-icon"><Icon size={14} /></span>{label}</span><strong dir="ltr">{time}</strong></div>)}
      </>}
    </div>
  </div>
);

export const LoginPage = () => {
  const { lang } = useParams();
  const { user, loading, login } = useAuth();
  const { language, setLanguage, t } = useI18n();
  const routeLang = normalizeLang(lang || getPreferredLang());
  const [selectedSystem, setSelectedSystem] = useState(0);
  const [signingIn, setSigningIn] = useState(false);
  const [loginError, setLoginError] = useState(false);
  const loginPending = useRef(false);
  const tabRefs = useRef([]);
  const activeSystem = SYSTEMS[selectedSystem];
  const rtl = language !== 'en';
  const reducedMotion = useReducedMotion();
  useLandingScroll(!user);

  useEffect(() => {
    const previousTitle = document.title;
    document.title = t('TNS Sentinel | مجتمعك، تحت قيادتك', 'TNS Sentinel | Your community. Your command.');
    const handlePageShow = () => {
      loginPending.current = false;
      setSigningIn(false);
    };
    window.addEventListener('pageshow', handlePageShow);
    return () => {
      document.title = previousTitle;
      window.removeEventListener('pageshow', handlePageShow);
    };
  }, [t]);

  const handleLogin = async () => {
    if (loginPending.current || loading) return;
    loginPending.current = true;
    setSigningIn(true);
    setLoginError(false);
    try {
      const started = await login();
      if (started === false) throw new Error('Login unavailable');
    } catch {
      setLoginError(true);
      setSigningIn(false);
      loginPending.current = false;
    }
  };

  const handleTabKey = (event, index) => {
    let next;
    if (event.key === 'ArrowRight') next = (index + (rtl ? -1 : 1) + SYSTEMS.length) % SYSTEMS.length;
    if (event.key === 'ArrowLeft') next = (index + (rtl ? 1 : -1) + SYSTEMS.length) % SYSTEMS.length;
    if (event.key === 'Home') next = 0;
    if (event.key === 'End') next = SYSTEMS.length - 1;
    if (next === undefined) return;
    event.preventDefault();
    setSelectedSystem(next);
    tabRefs.current[next]?.focus();
  };

  const loginButton = (className = '', testId) => (
    <button className={'sentinel-button ' + className} onClick={handleLogin} disabled={loading || signingIn} aria-busy={loading || signingIn} data-testid={testId}>
      {loading || signingIn ? <Loader2 size={18} className="sentinel-loading" /> : <ArrowUpRight size={18} />}
      {signingIn ? t('جارٍ الاتصال…', 'Connecting…') : t('الدخول عبر Discord', 'Continue with Discord')}
    </button>
  );

  if (user && !loading) return <Navigate to={buildDashboardPath(routeLang, 'profile')} replace />;

  return (
    <div className="sentinel-landing" dir={rtl ? 'rtl' : 'ltr'}>
      <a className="sentinel-skip" href="#sentinel-main">{t('انتقل إلى المحتوى', 'Skip to content')}</a>
      <header className="sentinel-header">
        <ReadingProgress />
        <div className="sentinel-wrap sentinel-header-inner">
          <a href="#sentinel-main" aria-label="TNS Sentinel"><Brand /></a>
          <nav className="sentinel-nav" aria-label={t('التنقل الرئيسي', 'Main navigation')}>
            <a href="#systems">{t('الأنظمة', 'Systems')}</a>
            <a href="#get-started">{t('البداية', 'Getting started')}</a>
            <a href="#questions">{t('الأسئلة الشائعة', 'FAQ')}</a>
          </nav>
          <div className="sentinel-header-actions">
            <button className="sentinel-language" onClick={() => setLanguage(rtl ? 'en' : 'ar')} aria-label={rtl ? 'Switch to English' : 'التبديل إلى العربية'} title={rtl ? 'English' : 'العربية'}><Globe2 size={15} /><span lang={rtl ? 'en' : 'ar'}>{rtl ? 'EN' : 'عربي'}</span></button>
            {loginButton('sentinel-button-small')}
          </div>
        </div>
      </header>
      <main id="sentinel-main" tabIndex={-1}>
        <CinematicHero t={t} loginButton={loginButton} assets={ASSETS} />
        <div className="sentinel-strip"><div className="sentinel-wrap sentinel-strip-inner">
          <span><ShieldCheck />{t('إشراف وحماية', 'Moderation & protection')}</span>
          <span><Zap />{t('مستويات ومكافآت', 'Levels & rewards')}</span>
          <span><UserPlus />{t('ترحيب بطابعك', 'Welcome, your way')}</span>
          <span><Globe2 />{t('بالعربية والإنجليزية', 'Arabic & English')}</span>
        </div></div>

        <SentinelManifesto t={t} />
        <section className="sentinel-section sentinel-wrap" id="systems" aria-labelledby="systems-title">
          <Reveal className="sentinel-section-head"><div><span className="sentinel-kicker">01 / {t('الأنظمة', 'THE SYSTEMS')}</span><h2 id="systems-title">{t('كل نظام. له دور.', 'Every system. A purpose.')}</h2></div><p>{t('من أول رسالة إلى أعلى مستوى. أدوات تعمل معًا لتبني مجتمعًا على مقاسك.', 'From the first message to the next milestone. Tools that work together for a community that feels like yours.')}</p></Reveal>
          <div className="sentinel-tabs" role="tablist" aria-label={t('أنظمة Sentinel', 'Sentinel systems')}>
            {SYSTEMS.map((system, index) => <button key={system.id} id={'tab-' + system.id} className="sentinel-tab" role="tab" aria-selected={selectedSystem === index} aria-controls={'panel-' + system.id} tabIndex={selectedSystem === index ? 0 : -1} ref={(element) => { tabRefs.current[index] = element; }} onClick={() => setSelectedSystem(index)} onKeyDown={(event) => handleTabKey(event, index)}><system.icon size={18} /><span>{t(...system.label)}</span><span className="sentinel-tab-index" aria-hidden="true">0{index + 1}</span>{selectedSystem === index && <motion.span className="sentinel-tab-line" layoutId="sentinel-active-tab" transition={{ type: "spring", stiffness: 380, damping: 34 }} />}</button>)}
          </div>
          {SYSTEMS.map((system, index) => <div key={system.id} id={'panel-' + system.id} role="tabpanel" aria-labelledby={'tab-' + system.id} hidden={selectedSystem !== index} tabIndex={0}>
            {selectedSystem === index && <motion.div key={activeSystem.id} className="sentinel-system" initial={reducedMotion ? false : { opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.65, ease: [0.22, 1, 0.36, 1] }}>
              <div className="sentinel-system-copy"><span className="sentinel-system-number" aria-hidden="true">0{index + 1}</span><h3>{t(...activeSystem.title)}</h3><p>{t(...activeSystem.description)}</p><div className="sentinel-points">{activeSystem.points.map((point) => <span key={point[1]}><Check size={15} />{t(...point)}</span>)}</div></div>
              <Reveal className="sentinel-preview-stage" delay={0.1}><SystemPreview id={activeSystem.id} t={t} /></Reveal>
            </motion.div>}
          </div>)}
          <div className="sentinel-more"><p>{t('والمزيد داخل لوحة التحكم', 'Also in your control panel')}</p><div><span><Command size={15} />{t('إدارة الأوامر', 'Command management')}</span><span><Users size={15} />{t('الرتب التلقائية', 'Automatic roles')}</span><span><Sparkles size={15} />{t('الرسائل المخصصة', 'Custom messages')}</span></div></div>
        </section>

        <section className="sentinel-section sentinel-approach" id="get-started" aria-labelledby="start-title"><div className="sentinel-wrap">
          <Reveal className="sentinel-section-head"><div><span className="sentinel-kicker">02 / {t('البداية', 'TAKE COMMAND')}</span><h2 id="start-title">{t('من هنا، تبدأ قيادتك.', 'Your next chapter starts here.')}</h2></div><p>{t('حساب Discord واحد. لوحة تحكم تجمع السيرفرات التي تملك صلاحية إدارتها.', 'One Discord account. A control panel for the servers you have permission to manage.')}</p></Reveal>
          <div className="sentinel-steps">
            {[
              ['01', t('ادخل بحسابك', 'Connect your account'), t('سجّل الدخول عبر Discord للوصول إلى حسابك ولوحة التحكم.', 'Sign in through Discord to access your account and control panel.')],
              ['02', t('اختر سيرفرك', 'Choose your server'), t('افتح قائمة سيرفراتك واختر السيرفر الذي تريد إدارته.', 'Open your server list and choose the community you want to manage.')],
              ['03', t('اضبطه على طريقتك', 'Make it yours'), t('فعّل الأنظمة، اضبط القواعد، وحدّد المكافآت التي تناسب مجتمعك.', 'Enable systems, set your rules, and choose the rewards that fit your community.')],
            ].map(([number, title, description]) => <Reveal className="sentinel-step" key={number} delay={Number(number) * 0.1}><span>{number}</span><h3>{title}</h3><p>{description}</p></Reveal>)}
          </div>
        </div></section>

        <section className="sentinel-section sentinel-wrap sentinel-faq-layout" id="questions" aria-labelledby="faq-title">
          <div className="sentinel-faq-heading"><span className="sentinel-kicker">03 / {t('قبل أن تبدأ', 'GOOD TO KNOW')}</span><h2 id="faq-title">{t('تفاصيل تهمّك.', 'A few things worth knowing.')}</h2><p>{t('إجابات واضحة، لتعرف ماذا ينتظرك داخل Sentinel.', 'A clear starting point for your time with Sentinel.')}</p></div>
          <div className="sentinel-faq">
            {[
              [t('ما هو TNS Sentinel؟', 'What is TNS Sentinel?'), t('بوت Discord ولوحة تحكم لإدارة مجتمعك، يجمع أنظمة المستويات والمكافآت والإشراف الآلي والترحيب والسجلات وإدارة الأوامر.', 'TNS Sentinel is a Discord bot and control panel for your community, bringing together leveling, rewards, automatic moderation, welcomes, logs, and command management.')],
              [t('من يستطيع إدارة السيرفر؟', 'Who can manage a server?'), t('الوصول يعتمد على ملكية السيرفر وصلاحيات Discord وصلاحيات لوحة التحكم الممنوحة لك. الأقسام المتاحة تختلف حسب دورك.', 'Access depends on server ownership, Discord permissions, and the dashboard permissions assigned to you. Available sections depend on your role.')],
              [t('هل يدعم العربية والإنجليزية؟', 'Does Sentinel support Arabic and English?'), t('نعم، يمكنك التبديل بين العربية والإنجليزية، مع دعم اتجاه القراءة المناسب في لوحة التحكم.', 'Yes. Switch between Arabic and English, with the corresponding reading direction in the control panel.')],
              [t('هل يمكن تخصيص كل نظام؟', 'Can I customize the systems?'), t('لكل نظام إعداداته. يمكنك ضبط مصادر الخبرة والمكافآت، قواعد الإشراف، رسائل الترحيب والسجلات بحسب الصلاحيات المتاحة لك.', 'Each system has its own settings. Configure XP sources and rewards, moderation rules, welcome messages, and logs according to your access permissions.')],
            ].map(([question, answer]) => <details key={question}><summary>{question}<Plus size={18} /></summary><p>{answer}</p></details>)}
          </div>
        </section>

        <section className="sentinel-finale" aria-labelledby="finale-title"><div className="sentinel-wrap"><span className="sentinel-kicker">TNS SENTINEL</span><h2 id="finale-title">{t('مجتمع يستحق أكثر.', 'Your community deserves more.')}</h2><p>{t('ابدأ من لوحة التحكم. والباقي على طريقتك.', 'Start with your control panel. Make the rest your own.')}</p>{loginButton()}</div></section>
      </main>
      <footer className="sentinel-footer"><div className="sentinel-wrap sentinel-footer-inner"><a href="#sentinel-main" aria-label="TNS Sentinel"><Brand /></a><small>{t('أداة مستقلة لإدارة مجتمعات Discord. غير تابعة لشركة Discord.', 'An independent tool for Discord communities. Not affiliated with Discord.')}</small><small dir="ltr">© {new Date().getFullYear()} TNS Sentinel</small></div></footer>
      {loginError && <div className="sentinel-error" role="alert"><LockKeyhole size={20} /><p>{t('تعذّر الاتصال بخدمة تسجيل الدخول. جرّب مرة أخرى بعد قليل.', 'Could not reach the sign-in service. Please try again shortly.')}</p><button onClick={() => setLoginError(false)} aria-label={t('إغلاق التنبيه', 'Dismiss alert')}><X size={18} /></button></div>}
    </div>
  );
};
